import { Helmet } from "react-helmet";
import React from "react";
import { z } from 'zod';
import { config } from "../../config";

const breadcrumbItemSchema = z.object({
  '@type': z.literal('ListItem'),
  position: z.number(),
  name: z.string(),
  item: z.string().optional(), // item is optional since it's not present in the last item
}).strict();

const breadcrumbListSchema = z.object({
  '@context': z.literal('https://schema.org'),
  '@type': z.literal('BreadcrumbList'),
  itemListElement: z.array(breadcrumbItemSchema).refine((items) => {
    // Ensure each item has an `item` property, except for the last one
    for (let i = 0; i < items.length; i++) {
      // Check if the item is not the last item
      if (i < items.length - 1 && !items[i].item) {
        return false; // Validation fails if an earlier item is missing `item`
      }
      // If it's the last item, `item` is allowed to be undefined
    }
    return true; // All validations passed
  }, {
    message: 'All items except the last one must have an "item" property.',
  }),
}).strict();


const mySchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Design Studio UI UX",
  url: "https://www.designstudiouiux.com/",
  logo: "https://www.designstudiouiux.com/images/logo-v2.png",
  contactPoint: {
    "@type": "ContactPoint",
    "telephone": "+1-917-730-4488",
    "contactType": "customer service",
    "areaServed": ["US","IN"]
  },
  sameAs: [
    "https://www.linkedin.com/company/design-studio-india/",
    "https://www.instagram.com/uiux.designstudio/",
    "https://x.com/design__studio"
  ],
});

export default function Seo({
  title,
  description,
  canonical,
  image = "https://www.designstudiouiux.com/images/icon.png",
  altText = "Design Studio",
  breadcrumbs = [],
  blogDetailsSchm = null,
  faqQuesAnsData,
  noIndex = false,
}) {

  if (config.exclude_form_index) {
    noIndex = true;
  }

  const breadcrumbSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((breadcrumb, index) => {
      const isLastItem = index === breadcrumbs.length - 1;
      return {
        "@type": "ListItem",
        position: index + 1,
        name: breadcrumb.name,
        ...(isLastItem ? {} : { item: breadcrumb.slug || "#" })
      };
    }),
  });

  try {
    breadcrumbListSchema.parse(JSON.parse(breadcrumbSchema));
  } catch (error) {
    console.log(error);
    throw new Error(`Invalid breadcrumb schema: ${error.message}`);
  }

  const blogDetailsSchema =
    blogDetailsSchm &&
    JSON.stringify({
      "@context": "https://schema.org/",
      "@type": "BlogPosting",
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `https://www.designstudiouiux.com/blog/${blogDetailsSchm.slug}/`,
      },
      headline: `${blogDetailsSchm.title}`,
      description: `${blogDetailsSchm.blogSeo.seoBlog.description}`,
      image: {
        "@type": "ImageObject",
        url: `https://www.designstudiouiux.com/${
          blogDetailsSchm?.featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData?.images?.fallback?.src
        }`,
      },
      author: {
        "@type": "Organization",
        name: "Design Studio",
      },
      publisher: {
        "@type": "Organization",
        name: "Design Studio",
        logo: {
          "@type": "ImageObject",
          url: "",
          width: "",
          height: "",
        },
      },
      datePublished: `${blogDetailsSchm.date}`,
      dateModified: `${blogDetailsSchm.modified}`,
    });

  const faqServiceSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqQuesAnsData && faqQuesAnsData.map((faqdata, index) => ({
      "@type": "Question",
      "name": faqdata.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faqdata.answer
      }
    })),
  });

  return (
    <Helmet htmlAttributes={{ lang: "en" }}>
      <title>{title || ""}</title>
      <meta name="description" content={description || ""} />
      <meta
        name="google-site-verification"
        content="ekRLBeboUMVHXAXiY4bxqugflTQXeHP4fmHVohFb-v4"
      />
      <meta
        name="google-site-verification"
        content="65cvAvroorMyMy23TqMLCPzmhKn098ddp_b24yMWOfo"
      />
      <meta name="msvalidate.01" content="5304C4AC75B74BD30F871C748F014172" />

      <link rel="canonical" href={canonical || ""} />

      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {!noIndex && <meta name="robots" content="index, follow" />}
      <link rel="alternate" href={canonical} hreflang="en" />

      <script type="application/ld+json">{mySchema}</script>
      {breadcrumbs.length && (
        <script type="application/ld+json">{breadcrumbSchema}</script>
      )}
      {blogDetailsSchema && (
        <script type="application/ld+json">{blogDetailsSchema}</script>
      )}
      {faqQuesAnsData !== undefined && (
        <script type="application/ld+json">{faqServiceSchema}</script>
      )}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content="Design Studio" />
      {altText && <meta property="og:image:alt" content={altText} />}
      {image && <meta property="og:image" content={image} />}
    </Helmet>
  );
}